// src/components/ImageLightbox.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import '../css/image-lightbox.css';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;
const WHEEL_THROTTLE_MS = 220;

// Просмотрщик изображений: открывается поверх страницы, вместо перехода в новую вкладку.
// Листается стрелками, колесом мыши, клавиатурой и полосой миниатюр.
const ImageLightbox = ({ images = [], initialIndex = 0, onClose }) => {
  const { t } = useTranslation();
  const items = useMemo(
    () => (Array.isArray(images) ? images.filter((img) => img && img.url) : []),
    [images]
  );
  const total = items.length;

  const [index, setIndex] = useState(() => Math.min(Math.max(initialIndex, 0), Math.max(total - 1, 0)));
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [broken, setBroken] = useState(false);
  const stageRef = useRef(null);
  const thumbsRef = useRef(null);
  const lastWheelRef = useRef(0);

  const current = items[index] || null;

  const goTo = useCallback(
    (next) => {
      if (total === 0) return;
      setIndex(((next % total) + total) % total);
      setZoom(MIN_ZOOM);
      setBroken(false);
    },
    [total]
  );

  const zoomBy = useCallback((delta) => {
    setZoom((prev) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round((prev + delta) * 10) / 10)));
  }, []);

  // Esc закрывает, стрелки листают, +/- масштабируют.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
      } else if (event.key === 'ArrowRight') {
        goTo(index + 1);
      } else if (event.key === 'ArrowLeft') {
        goTo(index - 1);
      } else if (event.key === '+' || event.key === '=') {
        zoomBy(ZOOM_STEP);
      } else if (event.key === '-') {
        zoomBy(-ZOOM_STEP);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [index, goTo, zoomBy, onClose]);

  // Пока открыт просмотрщик, фон не скроллится.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // Активная миниатюра всегда в зоне видимости.
  useEffect(() => {
    const strip = thumbsRef.current;
    if (!strip) return;
    const active = strip.querySelector('.image-lightbox-thumb.active');
    active?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [index]);

  // При зуме прокрутка работает штатно, без зума колесо листает фото.
  const handleWheel = (event) => {
    if (zoom > MIN_ZOOM || total <= 1) return;
    const now = Date.now();
    if (now - lastWheelRef.current < WHEEL_THROTTLE_MS) return;
    if (Math.abs(event.deltaY) < 4) return;
    lastWheelRef.current = now;
    goTo(index + (event.deltaY > 0 ? 1 : -1));
  };

  if (typeof document === 'undefined' || !current) return null;

  const zoomedStyle = zoom > MIN_ZOOM
    ? { width: `${zoom * 100}%`, maxWidth: 'none', maxHeight: 'none' }
    : undefined;

  return createPortal(
    <div
      className="image-lightbox-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('imageViewer.title')}
      onClick={() => onClose?.()}
    >
      <div className="image-lightbox" onClick={(event) => event.stopPropagation()}>
        <div className="image-lightbox-header">
          <div className="image-lightbox-title">
            <Icon name="image" size={14} />
            <span className="image-lightbox-name">{current.name || t('imageViewer.title')}</span>
          </div>
          <div className="image-lightbox-tools">
            {total > 1 && (
              <span className="image-lightbox-counter">{`${index + 1} / ${total}`}</span>
            )}
            <button
              type="button"
              className="image-lightbox-tool"
              onClick={() => zoomBy(-ZOOM_STEP)}
              disabled={zoom <= MIN_ZOOM}
              title={t('imageViewer.zoomOut')}
              aria-label={t('imageViewer.zoomOut')}
            >
              <Icon name="minus-circle" size={16} />
            </button>
            <span className="image-lightbox-zoom-value">{`${Math.round(zoom * 100)}%`}</span>
            <button
              type="button"
              className="image-lightbox-tool"
              onClick={() => zoomBy(ZOOM_STEP)}
              disabled={zoom >= MAX_ZOOM}
              title={t('imageViewer.zoomIn')}
              aria-label={t('imageViewer.zoomIn')}
            >
              <Icon name="plus-circle" size={16} />
            </button>
            <button
              type="button"
              className="image-lightbox-tool image-lightbox-close"
              onClick={() => onClose?.()}
              title={t('common.close')}
              aria-label={t('common.close')}
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        </div>

        <div className="image-lightbox-body">
          <div
            className={`image-lightbox-stage${zoom > MIN_ZOOM ? ' zoomed' : ''}`}
            ref={stageRef}
            onWheel={handleWheel}
          >
            {broken ? (
              <div className="image-lightbox-broken">
                <Icon name="alert-triangle" size={28} />
                <span>{t('imageViewer.loadError')}</span>
              </div>
            ) : (
              <img
                key={current.url}
                src={current.url}
                alt={current.name || ''}
                className="image-lightbox-image"
                style={zoomedStyle}
                onError={() => setBroken(true)}
                onDoubleClick={() => setZoom((prev) => (prev > MIN_ZOOM ? MIN_ZOOM : 2))}
              />
            )}
          </div>

          {total > 1 && (
            <>
              <button
                type="button"
                className="image-lightbox-nav prev"
                onClick={() => goTo(index - 1)}
                title={t('imageViewer.prev')}
                aria-label={t('imageViewer.prev')}
              >
                <Icon name="chevron-left" size={20} />
              </button>
              <button
                type="button"
                className="image-lightbox-nav next"
                onClick={() => goTo(index + 1)}
                title={t('imageViewer.next')}
                aria-label={t('imageViewer.next')}
              >
                <Icon name="chevron-right" size={20} />
              </button>
            </>
          )}
        </div>

        {total > 1 && (
          <div className="image-lightbox-thumbs" ref={thumbsRef}>
            {items.map((image, thumbIndex) => (
              <button
                key={`${image.url}-${thumbIndex}`}
                type="button"
                className={`image-lightbox-thumb${thumbIndex === index ? ' active' : ''}`}
                onClick={() => goTo(thumbIndex)}
                title={image.name || `${thumbIndex + 1}`}
              >
                <img src={image.url} alt="" loading="lazy" decoding="async" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ImageLightbox;
